import { tgpu, d, std, common } from 'typegpu';
import type { TgpuRoot, TgpuBindGroup, TgpuBindGroupLayout, TgpuRenderPipeline, TgpuBuffer } from 'typegpu';
import type { StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray, Vec4f } from 'typegpu/data';
import type { VizMode } from '../config/schema';
import { DataCell } from '../systems/shared/DataCell';
import { DisplayUniforms } from './DisplayTypes';
import { applyColormap } from './colormaps';
import { applyTonemap } from './tonemap';

function gpuVizModeIndex(vm: VizMode): number {
  switch (vm) {
    case 'distance': return 0;
    case 'divergence': return 1;
    case 'divergenceDistance': return 2;
    case 'position': return 3;
    case 'furthestNeighbor': return 4;
    case 'totalNeighborDistance': return 5;
    case 'closenessOfReturn': return 6;
    case 'ftle': return 7;
  }
}

type DataArrType = WgslArray<typeof DataCell>;
type DisplayBGL = TgpuBindGroupLayout<{
  data: { storage: DataArrType };
  params: { uniform: typeof DisplayUniforms };
}>;

export class DisplayRenderer {
  private root: TgpuRoot;
  private layout: DisplayBGL;
  private pipeline!: TgpuRenderPipeline<Vec4f>;
  private paramsBuffer: TgpuBuffer<typeof DisplayUniforms> & UniformFlag;
  private bindGroup: TgpuBindGroup;
  private dataArrType: DataArrType;

  constructor(root: TgpuRoot, dataArray: TgpuBuffer<DataArrType> & StorageFlag, cellCount: number) {
    this.root = root;
    this.dataArrType = d.arrayOf(DataCell, cellCount);
    this.paramsBuffer = root.createBuffer(DisplayUniforms, {
      maxValue: 1, colormap: 6, toneMapping: 0, vizMode: 0,
      resolution: 512, _pad0: 0, _pad1: 0, _pad2: 0,
    }).$usage('uniform') as unknown as TgpuBuffer<typeof DisplayUniforms> & UniformFlag;
    this.layout = tgpu.bindGroupLayout({
      data: { storage: this.dataArrType },
      params: { uniform: DisplayUniforms },
    }) as unknown as DisplayBGL;
    this.bindGroup = root.createBindGroup(this.layout, { data: dataArray, params: this.paramsBuffer });

    const displayLayout = this.layout;

    const fragment = ({ uv }: { uv: d.v2f }) => {
      'use gpu';
      const params = displayLayout.$.params;
      const res = params.resolution;
      const px = std.min(d.u32(uv.x * res), d.u32(res) - 1);
      const py = std.min(d.u32((1.0 - uv.y) * res), d.u32(res) - 1);
      const idx = py * d.u32(res) + px;
      const cell = displayLayout.$.data[idx];
      const vm = params.vizMode;
      if (vm < 0.5) {
        if (cell.a < 0.5) return d.vec4f(0.1, 0.1, 0.1, 1);
        const ct = std.select(std.clamp(applyTonemap(cell.b, params.maxValue, params.toneMapping), 0.0, 1.0), 0.0, false);
        const color = applyColormap(1.0 - ct, params.colormap);
        return d.vec4f(color.r, color.g, color.b, 1);
      } else if (vm < 1.5) {
        if (cell.g < 0.5) return d.vec4f(1, 1, 1, 1);
        const t = std.clamp(cell.r / params._pad0, 0.0, 1.0);
        const color = applyColormap(1.0 - t, params.colormap);
        return d.vec4f(color.r, color.g, color.b, 1);
      } else if (vm < 2.5) {
        if (cell.g < 0.5) return d.vec4f(1, 1, 1, 1);
        const t = std.clamp(applyTonemap(cell.b, params.maxValue, params.toneMapping), 0.0, 1.0);
        const color = applyColormap(1.0 - t, params.colormap);
        return d.vec4f(color.r, color.g, color.b, 1);
      } else if (vm < 3.5) {
        const angle = std.atan2(cell.g, cell.r);
        const t = angle / 6.28318530718 + 0.5;
        const color = applyColormap(t, params.colormap);
        return d.vec4f(color.r, color.g, color.b, 1);
      } else {
        if (cell.a < 0.5) return d.vec4f(0.08, 0.08, 0.08, 1);
        const ct = std.clamp(applyTonemap(cell.b, params.maxValue, params.toneMapping), 0.0, 1.0);
        const color = applyColormap(ct, params.colormap);
        return d.vec4f(color.r, color.g, color.b, 1);
      }
    };

    this.pipeline = root.createRenderPipeline({
      vertex: common.fullScreenTriangle,
      fragment,
    });
  }

  update(dataArray: TgpuBuffer<DataArrType> & StorageFlag): void {
    this.layout = tgpu.bindGroupLayout({
      data: { storage: this.dataArrType },
      params: { uniform: DisplayUniforms },
    }) as unknown as DisplayBGL;
    this.bindGroup = this.root.createBindGroup(this.layout, { data: dataArray, params: this.paramsBuffer });
  }

  setParams(maxValue: number, colormap: number, toneMapping: number, vizMode: VizMode, resolution: number, maxIter: number): void {
    this.paramsBuffer.write({ maxValue, colormap, toneMapping, vizMode: gpuVizModeIndex(vizMode), resolution, _pad0: maxIter, _pad1: 0, _pad2: 0 } as never);
  }

  render(canvasContext: GPUCanvasContext): void {
    this.pipeline
      .withColorAttachment({ view: canvasContext, clearValue: [0, 0, 0, 1] })
      .with(this.bindGroup)
      .draw(3);
  }
}
